import React, { FC } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DownloadIcon from "@mui/icons-material/Download";
import { SurfaceCard, PillButton } from "../ui";
import type { InstallationGuide } from "../../data/installation-guides";

export interface GuideCardProps {
  guide: InstallationGuide;
  /** Outlines the card — used when /guides is opened with ?product=<key>. */
  highlighted?: boolean;
}

/**
 * One product's installation guide, offered as a direct PDF download.
 *
 * The card keys off `pdf` rather than `guideUrl`: downloading the document is
 * the card's only action, so a product without a PDF has nothing to offer here
 * and renders greyed out. The `/support-docs/` permalink is still used by
 * `GuideLink` for inline references elsewhere in the app.
 */
const GuideCard: FC<GuideCardProps> = ({ guide, highlighted }) => {
  const { pdf } = guide;

  // The meta slot carries the document's facts when there is one, and the reason
  // there isn't otherwise — so both states occupy the same line and the cards
  // stay aligned in the grid.
  const meta = pdf
    ? [guide.steps ? `${guide.steps} steps` : null, pdf.sizeLabel]
        .filter(Boolean)
        .join(" · ")
    : "Guide not published yet";

  return (
    <SurfaceCard
      interactive={Boolean(pdf)}
      sx={{
        ...(highlighted && {
          borderColor: "primary.main",
          boxShadow: "0 0 0 3px rgba(236,170,29,0.18)",
        }),
        // Same frame as every other card, just greyed out.
        ...(!pdf && { bgcolor: "background.default" }),
      }}
    >
      <Typography
        variant="h4"
        component="p"
        sx={{ mb: 0.75, color: pdf ? "text.primary" : "text.disabled" }}
      >
        {guide.title}
      </Typography>

      <Typography
        variant="subtitle2"
        sx={{ mb: 0.5, color: pdf ? "text.primary" : "text.disabled" }}
      >
        {guide.subtitle}
      </Typography>

      <Typography variant="caption" sx={{ display: "block", mb: 2.5 }}>
        {meta}
      </Typography>

      <Box sx={{ mt: "auto" }}>
        {pdf ? (
          <PillButton
            tone="amber"
            size="small"
            component="a"
            href={pdf.url}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
            aria-label={`Download the Artome ${guide.title} installation guide PDF, ${pdf.sizeLabel}`}
          >
            Download PDF
          </PillButton>
        ) : (
          // Disabled rather than absent, so the card keeps the same shape as its
          // neighbours. No aria-label naming a document, since there isn't one.
          <PillButton tone="amber" size="small" disabled>
            Download PDF
          </PillButton>
        )}
      </Box>
    </SurfaceCard>
  );
};

export default GuideCard;
